import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useCallback } from "react";

import { toast } from "@performyard/py-components";
import type {
	CompensationCycleListResponse,
	CompensationCycleResponse,
	CompensationSettingsResponse,
	CreateCompensationCycleRequest,
	DeleteCompensationCycleResponse,
	GetSelectionManagersResponse,
	GetUsersWithoutCompensationDataResponse,
	MyDecisionsResponse,
	SelectionSalarySummaryResponse,
	SubmitDecisionsRequest,
	UpdateMyDecisionsNotesRequest,
} from "@performyard/service-contracts";
import type { ConvertObjectIdsAndDatesToString } from "@performyard/utils-isomorphic";

import type { DecisionRow, IncreaseValues } from "../../../pages/Compensation/CycleDetails/CellRenderers";

import {
	compensationCycleDetailsQuery,
	compensationCycleListQuery,
	compensationCycleListWithMyDecisionsQuery,
	compensationMyDecisionDetailsQuery,
	compensationPermissionsQuery,
	compensationSettingsQuery,
	getUsersWithoutCompensationDataQuery,
	selectionManagersQuery,
	selectionSalarySummaryQuery,
} from "../../queries";
import {
	addCompensationCycleOwner,
	createCompensationCycle,
	deleteCompensationCycle,
	dismissSalaryFieldVisibilityConflictBanner,
	getCompanySalaryCustomFieldData,
	getUserSalaryBannerDismissalData,
	removeCompensationCycleOwner,
	submitDecisions,
	updateCompensationCycleOwners,
	updateMyDecisionsNotes,
} from "../../services/defs/internal-api/compensation";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

type MyDecisionsDetailsData = ConvertObjectIdsAndDatesToString<MyDecisionsResponse>;

export const useCompensationSettings = () => {
	return useQuery<CompensationSettingsResponse, Error>(compensationSettingsQuery());
};

export const useCompensationPermissions = () => {
	return useQuery<CompensationSettingsResponse, Error>(compensationPermissionsQuery());
};

type CycleOwnersMutationInput = {
	cycleOwnerIds: string[];
	/** Hint for optimistic UI when adding a new owner */
	newOwner?: { _id: string; name: string };
};

export const useUpdateCompensationCycleOwners = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "cycle-owners"],
		mutationFn: async ({ cycleOwnerIds }: CycleOwnersMutationInput) => {
			return updateCompensationCycleOwners(cycleOwnerIds);
		},
		onMutate: async ({ cycleOwnerIds, newOwner }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const existingById = new Map(previous.cycleOwners.map((o) => [o._id, o]));
				// Keep all admin entries at the beginning (they aren't in cycleOwnerIds since they're auto-included)
				const adminOwners = previous.cycleOwners.filter((o) => o.isAdmin);
				const explicitOwners = cycleOwnerIds.map((id) => {
					const existing = existingById.get(id);
					if (existing) return existing;
					if (newOwner && newOwner._id === id) {
						return { _id: id, name: newOwner.name, role: "Loading..." as const, isAdmin: false };
					}
					return { _id: id, name: "", role: "Loading..." as const, isAdmin: false };
				});
				// Merge admins first, then explicit owners (no client-side sorting)
				const explicitIdSet = new Set(explicitOwners.map((o) => o._id));
				const merged = [...adminOwners.filter((o) => !explicitIdSet.has(o._id)), ...explicitOwners];

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: merged as CompensationSettingsResponse["cycleOwners"],
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

export const useSelectionManagers = (
	userSelectionId: string,
	params?: {
		skip?: number;
		limit?: number;
		sortBy?: "name" | "directReportCount" | "totalCompensation";
		sortOrder?: "asc" | "desc";
		search?: string;
	},
	options?: PYUseQueryOptions<GetSelectionManagersResponse>
) => {
	return useQuery<GetSelectionManagersResponse, Error>({
		...selectionManagersQuery(userSelectionId, params),
		enabled: !!userSelectionId,
		...options,
	});
};

export const useSelectionSalarySummary = (
	userSelectionId: string,
	options?: PYUseQueryOptions<SelectionSalarySummaryResponse>
) => {
	return useQuery<SelectionSalarySummaryResponse, Error>({
		...selectionSalarySummaryQuery(userSelectionId),
		enabled: !!userSelectionId,
		...options,
	});
};

export const useCreateCompensationCycle = (
	options?: PYUseMutationOptions<CompensationCycleResponse, Error, CreateCompensationCycleRequest>
) => {
	return useMutation({
		mutationKey: ["compensation-cycles", "create"],
		mutationFn: (data: CreateCompensationCycleRequest) => createCompensationCycle(data),
		meta: {
			errorMessage: "There was an error creating the compensation cycle.",
		},
		...options,
	});
};

// Admins and Cycle Owners can view all compensation cycles
export const useCompensationCycleList = (
	params: {
		skip?: number;
		limit?: number;
		sort?: string;
		search?: string;
		status?: string;
	},
	options?: PYUseQueryOptions<CompensationCycleListResponse>
) => {
	return useQuery<CompensationCycleListResponse, Error>({
		...compensationCycleListQuery(params),
		...options,
	});
};

// managers/acting managers can only view compensation cycles in which they have decisions to make
export const useCompensationCycleListWithMyDecisions = (
	params: {
		skip?: number;
		limit?: number;
		sort?: string;
		search?: string;
		status?: string;
	},
	options?: PYUseQueryOptions<CompensationCycleListResponse>
) => {
	return useQuery<CompensationCycleListResponse, Error>({
		...compensationCycleListWithMyDecisionsQuery(params),
		...options,
	});
};

export const useDeleteCompensationCycle = () => {
	const queryClient = useQueryClient();

	return useMutation<DeleteCompensationCycleResponse, Error, string>({
		mutationKey: ["compensation-cycles", "delete"],
		mutationFn: (cycleId: string) => deleteCompensationCycle(cycleId),
		meta: {
			errorMessage: "There was an error deleting the compensation cycle.",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
		},
	});
};

type AddCycleOwnerMutationInput = {
	userId: string;
	userName: string;
};

export const useAddCompensationCycleOwner = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "add-cycle-owner"],
		mutationFn: async ({ userId }: AddCycleOwnerMutationInput) => {
			return addCompensationCycleOwner(userId);
		},
		onMutate: async ({ userId, userName }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const exists = previous.cycleOwners.some((o) => o._id === userId);
				if (exists) return { previous };

				const newOwner = {
					_id: userId,
					name: userName,
					role: "Loading..." as const,
					isAdmin: false,
				};

				// Append to end - backend will re-sort by role on success
				const updatedOwners = [...previous.cycleOwners, newOwner];

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: updatedOwners,
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

type RemoveCycleOwnerMutationInput = {
	userId: string;
};

export const useRemoveCompensationCycleOwner = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "remove-cycle-owner"],
		mutationFn: async ({ userId }: RemoveCycleOwnerMutationInput) => {
			return removeCompensationCycleOwner(userId);
		},
		onMutate: async ({ userId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const updatedOwners = previous.cycleOwners.filter((o) => o._id !== userId);

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: updatedOwners,
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

export const useCompensationCycleDetails = (
	cycleId: string,
	options?: PYUseQueryOptions<CompensationCycleResponse>
) => {
	return useQuery<ConvertObjectIdsAndDatesToString<CompensationCycleResponse>, Error>({
		...compensationCycleDetailsQuery(cycleId),
		...options,
	});
};

export const useMyDecisionDetails = (
	userId: string,
	cycleId: string,
	options?: PYUseQueryOptions<MyDecisionsDetailsData>
) => {
	return useQuery<MyDecisionsDetailsData, Error>({
		...compensationMyDecisionDetailsQuery(userId, cycleId),
		...options,
	});
};
export const useUsersWithoutCompensationData = () => {
	return useQuery<GetUsersWithoutCompensationDataResponse>(getUsersWithoutCompensationDataQuery());
};

export const useCompanySalaryCustomFieldData = () => {
	return useQuery({
		queryKey: ["company-salary-custom-field"],
		queryFn: () => getCompanySalaryCustomFieldData(),
		meta: {
			errorMessage: "There was an error getting the company fields.",
		},
	});
};

export const useUserBannerDismissalData = () => {
	return useQuery({
		queryKey: ["user-salary-vis-conflict-banner-dismissal-data"],
		queryFn: () => getUserSalaryBannerDismissalData(),
		meta: {
			errorMessage: "There was an error getting the company fields.",
		},
	});
};

export const useDismissSalaryFieldVisibilityConflictBanner = () => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: dismissSalaryFieldVisibilityConflictBanner,
		onSuccess: () => {
			client.invalidateQueries({ queryKey: ["user-salary-vis-conflict-banner-dismissal-data"] });
		},
	});
};

export const useUpdateMyDecisionsNotes = () => {
	return useMutation({
		mutationKey: ["compensation", "my-decisions-notes", "update"],
		mutationFn: (data: UpdateMyDecisionsNotesRequest) => updateMyDecisionsNotes(data),
		meta: {
			errorMessage: "There was an error saving decision notes.",
		},
	});
};

export const useSubmitDecisions = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-decision"];

	return useMutation({
		mutationKey: ["compensation-decision"],
		mutationFn: async (data: SubmitDecisionsRequest) => {
			return submitDecisions(data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

const mapDecisionRowsToSubmitPayload = (rows: DecisionRow[], cycleIdFallback: string): SubmitDecisionsRequest =>
	rows
		.filter((row) => row.superRow !== true)
		.map((row) => {
			const proposed_increase_amount = row.proposed_increase_amount;
			const proposed_increase_percent = row.proposed_increase_percent;
			if (proposed_increase_amount == null || proposed_increase_percent == null) {
				throw new Error("Submit called with decision rows missing increase amount or percent");
			}
			return {
				_id: row._id,
				current_salary: row.current_salary,
				employee_id: row.employee_id,
				notes: row.notes ?? "",
				proposed_increase_amount,
				proposed_increase_percent,
				proposed_salary: row.proposed_salary ?? row.current_salary,
				compensation_cycle_id: row.compensation_cycle_id ?? cycleIdFallback,
			};
		});

type SubmitDecisionsMutate = (
	variables: SubmitDecisionsRequest,
	options?: {
		onSuccess?: () => void | Promise<void>;
		onError?: () => void;
	}
) => void;

export type UseDecisionSubmitParams = {
	cycleId: string | undefined;
	userID: string | undefined;
	groupDecisionRowsByManagerId: Record<string, DecisionRow[]>;
	submitDecisions: SubmitDecisionsMutate;
	setGroupCannotBeSubmitted: Dispatch<SetStateAction<string | null>>;
	setRefreshingGroupIds: Dispatch<SetStateAction<Record<string, boolean>>>;
	setDraftOverrides: Dispatch<SetStateAction<IncreaseValues>>;
};

export const useDecisionSubmit = ({
	cycleId,
	userID,
	groupDecisionRowsByManagerId,
	submitDecisions: submitDecisionsMutate,
	setGroupCannotBeSubmitted,
	setRefreshingGroupIds,
	setDraftOverrides,
}: UseDecisionSubmitParams): ((managerId: string) => void) => {
	const queryClient = useQueryClient();

	return useCallback(
		(managerId: string) => {
			setGroupCannotBeSubmitted(null);
			const groupRows = groupDecisionRowsByManagerId[managerId] ?? [];
			if (!groupRows.length || !userID || !cycleId) {
				toast.error("Error occurred while submitting decisions.");
				return;
			}
			setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: true }));
			submitDecisionsMutate(mapDecisionRowsToSubmitPayload(groupRows, cycleId), {
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: compensationMyDecisionDetailsQuery(userID, cycleId).queryKey,
					});

					const submittedRowIds = new Set(groupRows.map((row) => row._id));
					setDraftOverrides(
						(prev) =>
							Object.fromEntries(
								Object.entries(prev).filter(([rowId]) => !submittedRowIds.has(rowId))
							) as IncreaseValues
					);
					setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: false }));
					toast.success("Your decisions have been submitted for approval");
				},
				onError: () => {
					setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: false }));
					toast.error("Error occurred while submitting decisions.");
				},
			});
		},
		[
			cycleId,
			groupDecisionRowsByManagerId,
			queryClient,
			setDraftOverrides,
			setGroupCannotBeSubmitted,
			setRefreshingGroupIds,
			submitDecisionsMutate,
			userID,
		]
	);
};
